<script lang="ts" setup>
import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import { useToast } from 'primevue/usetoast';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useClinicianStudentStore, useArchiveStore } from '@/store';
import { StudentInterface } from '@/types';
import { EMIT, PERMISSIONS } from '@/enum';
import { can, isValidForRoleByTenant } from '@/composables';

interface StudentPropsInterface {
  tableData?: StudentInterface[];
  clinicianId?: number | undefined;
  loading?: boolean;
}

interface StudentTableInterface {
  id: number;
  display_name: string;
  service?: string;
  is_group?: boolean;
  grade?: string;
  age: number;
}

const props = defineProps<StudentPropsInterface>();
const emit = defineEmits<{ (e: EMIT.UPDATE): void }>();
const clinicianStudentStore = useClinicianStudentStore();
const archiveStore = useArchiveStore();
const students: Ref<StudentInterface[] | undefined> = toRef(props, 'tableData');
const studentCount: Ref<number> = computed(() => (students.value ? students.value.length : 0));
const studentModalOpen = ref(false);
const addStudentDataLoading = ref(false);
const toast = useToast();
const studentItems: Ref<{ name: string; value: number }[]> = ref([]);

const createMenuModel = (item: StudentTableInterface) => {
  return [
    {
      label: 'Archive Student',
      command: () => archiveStudent(item.id, item.display_name),
      visible: can([PERMISSIONS.PEOPLE_ARCHIVE]),
    },
    {
      label: 'Remove Student',
      command: () => detachStudent([item.id], item.display_name),
      visible: can([PERMISSIONS.CLINICIAN_DETACH_STUDENTS]),
    },
    {
      //TODO: Add logic to show this menu item and permissions
      label: 'Edit Completed Goal',
    },
    {
      //TODO: Add logic to show this menu item and
      label: 'Expired Goal',
    },
  ].filter((item) => item.visible);
};

const archiveStudent: (id: number, student: string) => Promise<void> = async (id: number, student: string) => {
  const archived_reason_id = 1;
  await archiveStore.archive(id, { archived_reason_id }).then(() => {
    emit(EMIT.UPDATE);
    doToast(false, `${student} Archived`);
  });
};

const attachStudent = async (ids: number[]) => {
  await clinicianStudentStore.attachStudents({
    students: ids,
    clinicianId: props.clinicianId as number,
  });
  emit(EMIT.UPDATE);
  doToast(false, 'Student(s) has been added');
};

const doToast = (error = false, msg: string) => {
  toast.add({
    severity: error ? 'error' : 'success',
    summary: error ? 'Error' : 'Saved',
    detail: msg,
    life: 5000,
  });
};

const detachStudent = async (ids: number[], studentName: string) => {
  await clinicianStudentStore.detachStudents({ students: ids, clinicianId: props.clinicianId as number });
  emit(EMIT.UPDATE);
  doToast(false, `${studentName} has been removed from service provider`);
};

const openAddStudents = async () => {
  studentItems.value = [];
  if (can([PERMISSIONS.CLINICIAN_STUDENTS_AVAILABLE_TO_ADD]) && can([PERMISSIONS.CLINICIAN_ATTACH_STUDENTS])) {
    addStudentDataLoading.value = true;
    studentModalOpen.value = true;
    studentItems.value = await clinicianStudentStore.getAddStudentsModalData(props.clinicianId as number);
    addStudentDataLoading.value = false;
  } else {
    doToast(true, 'You do not have permission to add students');
  }
};

const onCancelAdd = () => (studentModalOpen.value = false);

const onSaveAdd = (students: { name: string; value: number }[]) => {
  const studentIds = students.map((student) => student.value);
  attachStudent(studentIds);
  studentModalOpen.value = false;
};

type TreatmentService = {
  service: { name: string };
};

const treatmentServices = (treatmentServices: TreatmentService[]) => {
  const treatmentServiceNames = treatmentServices.map((item: TreatmentService) => item.service.name);
  const uniqueTreatmentServiceNames = [...new Set(treatmentServiceNames)];

  return uniqueTreatmentServiceNames.join(', ');
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Students</h2>
      <Chip>{{ studentCount }}</Chip>
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.CLINICIAN_ATTACH_STUDENTS]) && isValidForRoleByTenant()"
          type="text"
          size="small"
          text="Add Student"
          icon-left="add"
          @click="openAddStudents"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="students"
        :global-filter-fields="['display_name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="loading"
        data-key="display_name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-store-students-table"
        state-storage="session"
        :class="{ 'table-loading': loading }"
      >
        <template v-if="studentCount">
          <Column
            body-style="max-width: 20rem"
            field="display_name"
            header="Student Name"
            header-style="max-width: 20rem; color: #3B4242;"
            sortable
          >
            <template #body="{ data }">
              <RouterLink :to="`/user/${data.id}`" class="font-semibold" v-text="data.display_name" />
            </template>
          </Column>
          <Column header="Service">
            <template #body="{ data }">
              <span v-text="treatmentServices(data.treatment_services)"></span>
            </template>
          </Column>
          <Column header="Grade">
            <template #body="{ data }">
              <span>{{ data.grade ? data.grade : '-' }}</span>
            </template>
          </Column>
          <Column header="Age">
            <template #body="{ data }">
              <span>{{ data.age != 0 ? data.age : '-' }}</span>
            </template>
          </Column>
          <Column
            body-class="justify-content-center mw-10rem"
            header-class="justify-content-center mw-10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu
                v-if="createMenuModel(data).length > 0"
                :menu-items="createMenuModel(data)"
                icon="more_horiz"
              />
            </template>
          </Column>
        </template>
        <template #empty>Students not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="studentModalOpen && can([PERMISSIONS.CLINICIAN_STUDENTS_AVAILABLE_TO_ADD]) && isValidForRoleByTenant()"
        title="Add students"
        button-text="Add students"
        placeholder="Search students"
        :loading="addStudentDataLoading"
        :items="studentItems"
        :searchable="true"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
}
</style>
